import Button from "react-bootstrap/Button";
import React, { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import {
  updateTemplate, // ✅ New API call to update template
  getUserPracticeById,
  uploadImage,
  deleteImage, // ✅ API call to delete old image
} from "../../api";
import { FaCheck, FaTimes } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";

function EditTemplateModal({ template, onHide, show, templateEdit, editedTemplate }) {
  const [notes, setNotes] = useState(template?.notes || []);
  const [selectedPractices, setSelectedPractices] = useState(
    template?.templatePracticeId || []
  );
  const [pracName, setPracName] = useState([]);
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState(null);
  const [templateData, setTemplateData] = useState({});
  const hasNotesChanged = React.useRef(false);
  const [oldURL, setOldURL] = useState(null);
  const [closeAlert, setCloseAlert] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (template) {
      setSelectedPractices(template.templatePracticeId || []);
      setTemplateData({
        _id: template._id,
        templateTitle: template.templateTitle || "",
        templateDescription: template.templateDescription || "",
        templateStatus: template.templateStatus || "Active",
        templatePracticeId: template.templatePracticeId || [],
        templateInitial: template.templateInitial || "initial",
        notes: template.notes || [],
      });

      // ✅ Only reset `notes` if they haven't been edited yet
      if (!hasNotesChanged.current) {
        setNotes(template.notes || []);
      }
    }
  }, [template]); // ✅ Now it won't reset `notes` unnecessarily

  useEffect(() => {
    async function loadData() {
      const token = sessionStorage.getItem("user");
      if (token) {
        const decoded = jwtDecode(token);
        try {
          const response = await getUserPracticeById(decoded._id); // ✅ Await the Promise
          console.log("response", response);
          setPracName(response); // ✅ Now setting actual data, not a Promise
        } catch (error) {
          console.error("⛔ Error fetching user practice:", error);
        }
      }
    }
    loadData(); // ✅ Call async function inside useEffect
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // ✅ General Template Validations
    if (!templateData.templateTitle)
      newErrors.templateTitle = "Template Title is required.";
    if (!templateData.templateDescription)
      newErrors.templateDescription = "Template Description is required.";
    if (!templateData.templateStatus)
      newErrors.templateStatus = "Template Status is required.";
    if (!templateData.templatePracticeId.length)
      newErrors.templatePracticeId = "At least one practice is required.";
    if (!templateData.templateInitial)
      newErrors.templateInitial = "Template Type is required.";

    // ✅ Notes Validations (Loop Through Sections)
    notes.forEach((note, index) => {
      if (!note.sessionsSection) {
        newErrors[`notes-${index}-sessionSection`] =
          "Section Name is required.";
      }

      if (
        note.sessionFieldType === "multiselect" &&
        (!note.sessionSectionNotes || note.sessionSectionNotes.length === 0)
      ) {
        newErrors[`notes-${index}-sessionSectionNotes`] =
          "At least one option is required.";
      }

      if (note.sessionFieldType === "image") {
        const file = note.sessionSectionNotes;

        if (!file) {
          newErrors[`notes-${index}-sessionSectionNotes`] =
            "Image upload is required.";
        } else {
          // ✅ Validate File Type
          const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
          if (!allowedTypes.includes(file.type)) {
            newErrors[`notes-${index}-sessionSectionNotes`] =
              "Only PNG, JPEG, and JPG files are allowed.";
          }

          // ✅ Validate File Size (Max 2MB)
          const maxSize = 2 * 1024 * 1024; // 2MB
          if (file.size > maxSize) {
            newErrors[`notes-${index}-sessionSectionNotes`] =
              "File size must be less than 2MB.";
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRemoveImage = async (imageUrl) => {
    try {
      if (!imageUrl) return;

      // ✅ Extract filename from URL
      const urlParts = imageUrl.split("/");
      const fileName = urlParts[urlParts.length - 1].split("?")[0]; // Remove query parameters if any

      if (!fileName) {
        console.warn("⚠️ No valid filename found in URL:", imageUrl);
        return;
      }

      console.log("🗑️ Deleting old image:", fileName);
      await deleteImage(fileName);
      console.log("✅ Old image deleted successfully.");
    } catch (error) {
      console.error("⛔ Failed to delete old image:", error.message);
    }
  };

 const handlePracticeChange = (practiceNumber) => {
  const updated = selectedPractices.includes(practiceNumber)
    ? selectedPractices.filter((p) => p !== practiceNumber)
    : [...selectedPractices, practiceNumber];

  setSelectedPractices(updated);

  // ←–– Sync it back into your templateData
  setTemplateData((prev) => ({
    ...prev,
    templatePracticeId: updated,
  }));
};


  const handleRemoveSection = async (index) => {
    const sectionToRemove = notes[index];

    // ✅ Ensure the section has an image before attempting deletion
    if (
      sectionToRemove.sessionsFieldType === "image" &&
      sectionToRemove.sessionsImageURL
    ) {
      try {
        // ✅ Extract ONLY the file name from the URL
        const imageUrl = new URL(sectionToRemove.sessionsImageURL);
        const fileName = imageUrl.pathname.split("/").pop(); // ✅ Get last part of URL (file name)

        console.log("🗑️ Deleting image:", fileName);
        await deleteImage(fileName); // ✅ Now passing correct file name
        console.log("✅ Image deleted successfully.");
      } catch (error) {
        console.error("⛔ Failed to delete image:", error.message);
      }
    }

    // ✅ Remove the section from state
    setNotes((prevNotes) => prevNotes.filter((_, i) => i !== index));
  };

  const handleSectionChange = (index, field, value) => {
    setNotes((prevNotes) => {
      const updatedNotes = [...prevNotes];
      updatedNotes[index][field] = value;
      return updatedNotes;
    });
    console.log("updatedNotes", notes);

    hasNotesChanged.current = true; // ✅ Mark notes as changed
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    let updatedNotes = [...notes];

    for (let i = 0; i < updatedNotes.length; i++) {
      if (
        updatedNotes[i].sessionsFieldType === "image" &&
        updatedNotes[i].sessionsSectionNotes // ✅ Check if a new file is selected
      ) {
        const imageFile = updatedNotes[i].sessionsSectionNotes;

        // ✅ Only delete & upload if the image has changed
        if (imageFile instanceof File) {
          // ✅ Upload new image
          const formData = new FormData();
          formData.append("image", imageFile);
          formData.append("x-image-caption", "Updated Template Image");

          try {
            console.log("📤 Uploading new image...");
            const response = await uploadImage(formData);
            updatedNotes[i].sessionsImageURL = response.imageUrl; // ✅ Save only the filename, not the full URL
            console.log("✅ New image uploaded:", response.imageUrl);
          } catch (error) {
            console.error("⛔ Upload failed:", error.message);
            setErrorAlert("Image upload failed. Please try again.");
            return;
          }
        }
      }
    }

    // ✅ Ensure `templateData` includes the latest `notes`
    const updatedTemplate = {
      ...templateData,
      notes: updatedNotes, // ✅ Ensuring latest `notes` are used
    };

    try {
      await updateTemplate(updatedTemplate._id, updatedTemplate);
      setSuccessAlert(true);
      
      setTimeout(() => {
        setSuccessAlert(false);
        onHide();
        
          templateEdit();  // ✅ Refresh template list & view
          editedTemplate(updatedTemplate); // ✅ Pass updated template to parent
      }, 2000);
      setTemplateData({
        _id: template._id,
        templateTitle: "",
        templateDescription: "",
        templateStatus: "Active",
        templatePracticeId: [],
        templateInitial: "initial",
        notes: [],
      });
    } catch (error) {
      setErrorAlert(error.message);
    }
  };

  function handleClose() {
    setCloseAlert(true);
  }

  const handleCancelNo = () => {
    setCloseAlert(false);
  };

  const handleCancelYes = () => {
    setTemplateData({
      templateTitle: "",
      templateDescription: "",
      templateStatus: "Active",
      templatePracticeId: "",
      templateInitial: "",
      notes: [],
    });
    setCloseAlert(false);
    setErrors({});
    onHide();
  };

  return (
    <Modal
      size="lg"
      centered
      aria-labelledby="contained-modal-title-vcenter"
      onHide={handleClose}
      show={show}
    >
      <Modal.Header
        closeButton={!closeAlert && !successAlert}
        className="border-secondary"
      >
        <div className="w-100">
          <Modal.Title
            id="contained-modal-title-vcenter"
            className="text-primary"
          >
            Edit Template
          </Modal.Title>
          
        </div>
      </Modal.Header>
{closeAlert && (
            <Alert
              variant="info"
              className="d-flex align-items-center justify-content-between fs-6 fw-normal custom-alert"
            >
              <span>
                Are you sure you want to close? Changes will not be saved.
              </span>
              <div className="ms-auto d-flex">
                <Button
                  variant="outline-primary"
                  className="me-1"
                  onClick={handleCancelYes}
                >
                  <FaCheck />
                </Button>
                <Button
                  variant="outline-secondary"
                  className="me-1"
                  onClick={handleCancelNo}
                >
                  <FaTimes />
                </Button>
              </div>
            </Alert>
          )}

          {successAlert && (
            <Alert
              variant="success"
              className="d-flex align-items-center justify-content-between fs-6 fw-normal custom-alert"
            >
              <span>The template was updated successfully!</span>
            </Alert>
          )}
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Label>Template Title</Form.Label>
            <Form.Control
              type="text"
              value={templateData.templateTitle}
              onChange={(e) =>
                setTemplateData({
                  ...templateData,
                  templateTitle: e.target.value,
                })
              }
              isInvalid={!!errors.templateTitle}
            />
            <Form.Control.Feedback type="invalid">
              {errors.templateTitle}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={templateData.templateDescription}
              onChange={(e) =>
                setTemplateData({
                  ...templateData,
                  templateDescription: e.target.value,
                })
              }
              isInvalid={!!errors.templateDescription}
            />
            <Form.Control.Feedback type="invalid">
              {errors.templateDescription}
            </Form.Control.Feedback>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Template Status</Form.Label>
                <Form.Select
                  isInvalid={!!errors.templateStatus}
                  value={templateData.templateStatus}
                  onChange={(e) =>
                    setTemplateData({
                      ...templateData,
                      templateStatus: e.target.value,
                    })
                  }
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.templateStatus}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Template Type</Form.Label>
                <Form.Select
                  isInvalid={!!errors.templateInitial}
                  value={templateData.templateInitial}
                  onChange={(e) =>
                    setTemplateData({
                      ...templateData,
                      templateInitial: e.target.value,
                    })
                  }
                >
                  <option value="initial">Initial Assessment Template</option>
                  <option value="followup">Follow Up Template</option>
                  <option value="reportexport">Report Export Template</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.templateInitial}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Form.Group as={Col} controlId="practiceCheckboxes">
              <Form.Label>Select Practices</Form.Label>
              <div>
                {pracName.map((prac) => (
                  <Form.Check
                    key={prac.practiceNumber}
                    type="checkbox"
                    label={prac.practiceName}
                    checked={selectedPractices.includes(prac.practiceNumber)}
                    onChange={() => handlePracticeChange(prac.practiceNumber)}
                    isInvalid={!!errors.templatePracticeId}
                  />
                ))}
              </div>
              <Form.Control.Feedback type="invalid">
                {errors.templatePracticeId}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* ✅ Render Template Sections */}
          <h6 className="text-primary mt-3">Template Sections</h6>
          <hr />
          {notes.map((note, index) => (
            <div key={index} className="mb-3">
              <Row className="align-items-end">
                <Col md={5}>
                  <Form.Group>
                    <Form.Label>Section Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={note.sessionsSection}
                      onChange={(e) =>
                        handleSectionChange(
                          index,
                          "sessionsSection",
                          e.target.value
                        )
                      }
                      isInvalid={!!errors[`notes-${index}-sessionSection`]} // ✅ Validation
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors[`notes-${index}-sessionSection`]}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={5}>
                  <Form.Group>
                    <Form.Label>Field Type</Form.Label>
                    <Form.Select
                      value={note.sessionsFieldType}
                      onChange={(e) =>
                        handleSectionChange(
                          index,
                          "sessionsFieldType",
                          e.target.value
                        )
                      }
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Text Area</option>
                      <option value="multiselect">Multi-Select</option>
                      <option value="image">Image</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* ✅ Dustbin Icon to Remove Section */}
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    variant=""
                    size="sm"
                    onClick={() => handleRemoveSection(index)}
                  >
                    <i className="fa-regular fa-trash-can text-danger fa-xl"></i>{" "}
                  </Button>
                </Col>
              </Row>

              {/* ✅ Show "Default Value" only if NOT an Image */}
              {note.sessionsFieldType !== "image" && (
                <Form.Group className="mt-2">
                  <Form.Label>Default Value</Form.Label>
                  <Form.Control
                    type="text"
                    value={note.sessionsSectionNotes}
                    onChange={(e) =>
                      handleSectionChange(
                        index,
                        "sessionsSectionNotes",
                        e.target.value
                      )
                    }
                  />
                </Form.Group>
              )}

              {/* ✅ Image Upload & Live Preview */}
              {note.sessionsFieldType === "image" && (
                <Form.Group className="mt-2">
                  <Form.Label>Upload New Image</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const updatedNotes = [...notes];

                        // ✅ Validate File Type
                        const allowedTypes = [
                          "image/png",
                          "image/jpeg",
                          "image/jpg",
                        ];
                        if (!allowedTypes.includes(file.type)) {
                          setErrors((prevErrors) => ({
                            ...prevErrors,
                            [`notes-${index}-sessionSectionNotes`]:
                              "Only PNG, JPEG, and JPG files are allowed.",
                          }));
                          return; // ❌ Stop execution if invalid type
                        }

                        // ✅ Validate File Size (Max 2MB)
                        const maxSize = 2 * 1024 * 1024; // 2MB
                        if (file.size > maxSize) {
                          setErrors((prevErrors) => ({
                            ...prevErrors,
                            [`notes-${index}-sessionSectionNotes`]:
                              "File size must be less than 2MB.",
                          }));
                          return; // ❌ Stop execution if file too large
                        }

                        // ✅ Reset Errors if Valid
                        setErrors((prevErrors) => {
                          const newErrors = { ...prevErrors };
                          delete newErrors[
                            `notes-${index}-sessionSectionNotes`
                          ];
                          return newErrors;
                        });

                        // ✅ Store existing image URL before changing it
                        const oldFileName =
                          updatedNotes[index].sessionsImageURL;
                        setOldURL(oldFileName);

                        // ✅ If `oldFileName` contains an actual filename, delete it
                        if (oldFileName && !oldFileName.includes("blob")) {
                          handleRemoveImage(oldFileName);
                        }

                        // ✅ Set new image preview
                        const imageUrl = URL.createObjectURL(file);
                        updatedNotes[index].sessionsSectionNotes = file;
                        updatedNotes[index].sessionsImageURL = imageUrl;
                        setNotes(updatedNotes);
                      }
                    }}
                    isInvalid={!!errors[`notes-${index}-sessionSectionNotes`]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[`notes-${index}-sessionSectionNotes`]}
                  </Form.Control.Feedback>

                  {/* ✅ Show the Image Preview */}
                  {note.sessionsImageURL && (
                    <div className="mt-2">
                      <img
                        src={note.sessionsImageURL}
                        alt="Uploaded Preview"
                        className="img-fluid rounded"
                        style={{ maxWidth: "100%", maxHeight: "300px" }}
                      />
                    </div>
                  )}
                </Form.Group>
              )}

              <hr />
            </div>
          ))}

          {/* ✅ Add New Section Button */}
          <div className="d-flex justify-content-start align-items-start mt-3">
            <Button
              variant="outline-secondary"
              className="w-25 mt-3 text-secondary"
              onClick={() => {
                setNotes([
                  ...notes,
                  {
                    sessionsSection: "",
                    sessionsFieldType: "text",
                    sessionsSectionNotes: "",
                  },
                ]);
              }}
            >
              + Add Section
            </Button>
          </div>

          <div className="d-flex justify-content-center align-items-center mt-3">
            <Button variant="primary" type="submit" className="mt-3 w-25">
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export function EditTemplate({ template, templateEdit, editedTemplate }) {
  const [templateModalShow, setTemplateModalShow] = useState(false);

  return (
    <div>
      <Button
        variant=""
        className="me-2 w-100"
        onClick={() => setTemplateModalShow(true)}
      >
        <i className="fas fa-edit text-primary"></i>
      </Button>
      <EditTemplateModal
        template={template} 
        show={templateModalShow}
        onHide={() => {
          setTemplateModalShow(false);
          
        }}
        templateEdit={templateEdit}// ✅ Pass down
        editedTemplate={editedTemplate}// ✅ Pass down
      />
    </div>
  );
}
